import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Typography, Paper, Box, CircularProgress } from '@mui/material';
import { styled } from '@mui/material/styles';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { verifyAccount } from '../services/api';

const StyledContainer = styled(Container)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '80vh',
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(6),
    textAlign: 'center',
    borderRadius: theme.spacing(2),
    boxShadow: '0 3px 5px 2px rgba(0, 0, 0, .1)',
    maxWidth: 500,
    width: '100%',
}));

const ErrorIcon = styled(ErrorOutlineIcon)(({ theme }) => ({
    fontSize: 80,
    color: theme.palette.error.main,
    marginBottom: theme.spacing(3),
}));

const LoadingContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(3),
}));

const VerifyAccount = () => {
    const { token } = useParams();
    const navigate = useNavigate();
    const [status, setStatus] = useState('loading');
    const [error, setError] = useState('');

    const handleSuccess = useCallback(() => {
        setStatus('success');
        const timeoutId = setTimeout(() => {
            navigate('/verify-success', { replace: true });
        }, 1500);
        return () => clearTimeout(timeoutId);
    }, [navigate]);

    const handleVerification = useCallback(async () => {
        try {
            const response = await verifyAccount(token);

            if (response.ok || response.status === 200 || response.status === 307) {
                return handleSuccess();
            }

            setStatus('error');
            setError('Verification failed. Please try again or contact support.');
        } catch (error) {
            console.error('Verification failed:', error);

            if (error.response?.status === 409) {
                return handleSuccess();
            }

            setStatus('error');
            setError(error.response?.data?.message ||
                'Verification failed. Please try again or contact support.');
        }
    }, [token, handleSuccess]);

    useEffect(() => {
        let mounted = true;
        let cleanup = () => { };

        const verify = async () => {
            if (mounted) {
                cleanup = await handleVerification() || (() => { });
            }
        };

        verify();

        return () => {
            mounted = false;
            cleanup();
        };
    }, [handleVerification]);

    const renderContent = () => {
        switch (status) {
            case 'loading':
                return (
                    <LoadingContainer>
                        <CircularProgress size={50} />
                        <Typography variant="h6" component="h2">
                            Verifying your account...
                        </Typography>
                        <Typography variant="body1" color="textSecondary">
                            This will only take a moment
                        </Typography>
                    </LoadingContainer>
                );

            case 'error':
                return (
                    <>
                        <ErrorIcon />
                        <Typography variant="h5" component="h2" gutterBottom>
                            Verification Failed
                        </Typography>
                        <Typography variant="body1" color="textSecondary">
                            {error}
                        </Typography>
                    </>
                );

            case 'success':
                return (
                    <LoadingContainer>
                        <CircularProgress size={50} />
                        <Typography variant="h6" component="h2">
                            Verification successful!
                        </Typography>
                        <Typography variant="body1" color="textSecondary">
                            Redirecting you...
                        </Typography>
                    </LoadingContainer>
                );

            default:
                return null;
        }
    };

    return (
        <StyledContainer maxWidth="sm">
            <StyledPaper elevation={3}>
                {renderContent()}
            </StyledPaper>
        </StyledContainer>
    );
};

export default VerifyAccount;